// Hand-written TypeScript mirror of the hunting-zones API surface
// (`backend/api/swagger.yaml`). There is no frontend codegen pipeline yet
// (root CLAUDE.md "API contract source of truth"), so every change to the
// swagger Zone / import schemas MUST be reflected here in the same PR.
// Field names stay snake_case to preserve the wire format.

export type ZoneCountry = 'FR' | 'IT' | 'BE' | 'CH' | 'DE' | 'ES' | 'NL' | 'AT' | 'LU' | 'CZ';

export type ZoneCountryFilter = ZoneCountry | 'unset';

export type ZoneGeometryType = 'Point' | 'MultiPoint' | 'Polygon';

/**
 * 5-state Notion sync lifecycle.
 * `syncing` = fetch + parse + build plan; `persisting` = DB transaction.
 */
export type ImportJobStatus = 'queued' | 'syncing' | 'persisting' | 'completed' | 'failed';

/**
 * Terminal sync statuses. The SSE stream closes once the job transitions
 * into one of these. The runtime helper to test against this set lives in
 * `@/features/zones/utils/isImportJobTerminal` (no runtime helpers in
 * types/).
 */
export const IMPORT_JOB_TERMINAL_STATUSES = ['completed', 'failed'] as const;

/** Per-row outcome of a single processed Notion page. */
export type SyncOutcomeDecision = 'created' | 'updated' | 'skipped' | 'failed';

/**
 * Machine-readable reason a row was skipped or failed. Null on
 * created / updated. The outcome screen maps each value to a label.
 */
export type SyncOutcomeReason =
  | 'name_missing'
  | 'geometry_missing'
  | 'geometry_invalid_json'
  | 'geometry_not_polygon'
  | 'geometry_invalid'
  | 'invalid_zone_payload';

export interface IGeoJSONGeometry {
  type: ZoneGeometryType;
  coordinates: number[] | number[][] | number[][][];
}

/**
 * Read-only hunting zone. The sole writer is the Notion
 * sync workflow; the API exposes no mutation. `last_synced_at` is the
 * timestamp the last sync touched this zone; `notion_page_url` is a deep
 * link to the source Notion page, derived server-side in the DTO mapper.
 */
export interface IZone {
  id: string;
  name: string;
  geometry: IGeoJSONGeometry;
  country: ZoneCountry | null;
  owner_id: string | null;
  created_at: string;
  updated_at: string;
  last_synced_at: string | null;
  notion_page_url?: string;
}

/**
 * Row shape of `GET /zones`: identity plus sync metadata, no geometry. The
 * zones vector tiles carry the geometry for the map; a single zone's exact
 * geometry comes from `GET /zones/{id}` as a full `IZone`.
 */
export interface IZoneSummary {
  id: string;
  name: string;
  country: ZoneCountry | null;
  owner_id: string | null;
  last_synced_at: string | null;
  notion_page_url?: string;
}

export interface IPaginatedZones {
  zones: IZoneSummary[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
}

export interface IZoneListFilter {
  /** Exact zone names. Serialises to repeated `name`. */
  names?: string[];
  countries?: ZoneCountryFilter[];
  /**
   * owners to keep — each entry is an owner id (their lowercased email), or
   * the `unset` sentinel for zones with no owner. Same idiom as
   * `ZoneCountryFilter`.
   */
  owner_ids?: string[];
  page?: number;
  limit?: number;
}

// ── Notion sync ─────────────────────────────────────────────────────────────

/** Running / final counts of a sync. Mirrors the SSE event `counts`. */
export interface ISyncCounts {
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  /** Zones removed this sync because their Notion page is gone. */
  deleted: number;
}

/** 202 body of `POST /zones/imports/notion`. */
export interface ICreateNotionSyncResponse {
  job_id: string;
  status: ImportJobStatus;
}

/**
 * One-shot JSON snapshot of `GET /zones/imports/jobs/{jobId}` (the
 * non-SSE fallback). Also the shape of each SSE `status` event's payload
 * (status + counts), so the stream hook can reuse it.
 */
export interface IImportJobSnapshot {
  id: string;
  status: ImportJobStatus;
  initiated_by_email: string;
  notion_database_id: string;
  started_at: string;
  completed_at?: string | null;
  failure_reason?: string | null;
  counts?: ISyncCounts;
}

/** One entry per Notion row processed during the sync. */
export interface ISyncOutcomeRow {
  decision: SyncOutcomeDecision;
  reason?: SyncOutcomeReason | null;
  notion_page_id: string;
  resulting_zone_id?: string | null;
}

/** Terminal per-row outcome of `GET /zones/imports/jobs/{jobId}/outcome`. */
export interface IImportJobOutcome {
  job_id: string;
  status: ImportJobStatus;
  counts: ISyncCounts;
  outcome_rows: ISyncOutcomeRow[];
}
